import type { ListBuilderConfiguration } from '../../types';
import { cartSuggestionsDefaultList } from './lists/cart-suggestions';
import { clientCartDefaultList } from './lists/client-cart-default';
import { invoiceCartDefaultList } from './lists/invoice-cart-default';
import { quoteCartDefaultList } from './lists/quote-cart-default';
import { createCartObjects } from './shared';

/**
 * The three cart line lists (PD-13666).
 *
 * Separate list keys so each surface keeps its own configuration — a tenant
 * that wants cost on the quote but not on the cart can have that — while the
 * columns themselves come from one shared module (`./columns`) so a change
 * reaches all three by default.
 *
 * No filter configuration and no `paging`: a cart is a single working set that
 * renders in one scroll, and the rows are already held client-side for totals,
 * credit checks and limit validation, so there is nothing to page or filter
 * server-side.
 */
const createCartConfiguration = (
	lists: ListBuilderConfiguration['lists'],
): ListBuilderConfiguration => ({
	lists,
	showResetButton: false,
	// A cart is a single working set: every line renders in one scroll, and the
	// totals, credit checks and limit validation in the sidebar are computed
	// across all of them. Without this the list falls back to the 20-row
	// default and a 34-line cart silently shows "1-20 of 34" (PD-13666).
	paging: {
		defaultPageSize: 1000,
		pageSizeOptions: [1000],
	},
	// No filter bar on a cart: the rows are already all present client-side and
	// the surface is a working set, not something you narrow.
	filters: {
		activeFilters: [],
		defaultFilters: {},
		availableFilters: [],
	},
	objects: createCartObjects(),
});

export const CLIENT_CART_CONFIG = createCartConfiguration([
	clientCartDefaultList,
]);

export const QUOTE_CART_CONFIG = createCartConfiguration([
	quoteCartDefaultList,
]);

export const INVOICE_CART_CONFIG = createCartConfiguration([
	invoiceCartDefaultList,
]);

/**
 * The suggestions strip. Its own key so it can be configured independently of
 * the cart lines above it — a tenant that wants a different column on the cart
 * does not necessarily want it on four suggestions.
 */
export const CART_SUGGESTIONS_CONFIG = createCartConfiguration([
	cartSuggestionsDefaultList,
]);
